/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import UserLineIcon from 'remixicon-react/UserLineIcon';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import ModalContainer from '../../../../atoms/Modal/ModalContainer';
import HeaderTitle from '../../../../atoms/Modal/HeaderTitle';
import FooterButtonContainer from '../../../../atoms/Modal/FooterButtonContainer';
import Button from '../../../../atoms/Button/Button';
import Toggle from '../../../../atoms/Toggle/Toggle';
import Spinner from '../../../../atoms/Spinner/Spinner';
import ErrorMessage from '../../../../atoms/Message/Error/ErrorMessage';
import ValidationError from '../../../../atoms/Validation/ValidationError';
import { ErrorType } from '../../../../../types/response/ErrorCodes';
import { hasMerchantName, Merchant } from '../../../../../types/onboarding/Merchant';
import { SalesSegment } from '../../../../../types/onboarding/OnboardStore';
import { PriceCountry } from '../../../../../constants/merchantPriceCatalog';
import { useOnboardingStore } from '../../../../../store/admin/onboarding/onboardingStore';
import {
    getMerchantPrices,
    saveMerchantPrices,
    syncMerchantPrices,
} from '../../../../../api/onboarding/merchantPrices';
import {
    PriceFormValues,
    salesSegments,
    segmentsShownFor,
    toMerchantPriceList,
    toPriceListDefaults,
} from '../../../../../services/helpers/merchantPrices';
import { formatOrganizationNumber } from '../../../../../services/helpers/organizationNumber';
import FlagIcon from '../../../../atoms/FlagIcon/FlagIcon';
import RefundFeeFields from './RefundFeeFields';
import CountryPriceTable from './CountryPriceTable';
import { transparentize } from 'polished';
import CheckboxCircleFillIcon from 'remixicon-react/CheckboxCircleFillIcon';
import { TableHeading } from './styles';

interface MerchantPricesModalProps {
    organizationId: string;
    merchant: Merchant;
    /** Whether the list can be edited and saved: internal users with write on the organization. */
    canEdit: boolean;
    /** Whether the sync to Edge is offered: internal users only. */
    isInternal: boolean;
    hide: () => void;
    theme: DefaultTheme;
}

const segmentIcons: Record<SalesSegment, React.ReactElement> = {
    B2C: <UserLineIcon size={16} />,
    B2B: <BuildingLineIcon size={16} />,
};

/** Every fee problem of the form, for the summary and the save gate. */
const priceErrorMessages = (errors: FieldErrors<PriceFormValues>): string[] => {
    const groups = Object.values(errors.fees ?? {}) as Record<
        string,
        { message?: unknown } | undefined
    >[];

    return groups
        .flatMap(group => Object.values(group ?? {}))
        .map(error => error?.message)
        .filter((message): message is string => typeof message === 'string');
};

/**
 * The price list of a merchant laid out like the product appendix of the merchant agreement: per market (country) and
 * sales segment the payment methods with their Edge product codes and fees, and for the whole list which fees are charged
 * on refunds too. Codes
 * are listed per market and segment, since the stores of a merchant are not set up the same way everywhere. The whole
 * grid is one react-hook-form form: the codes are a field array per method, market and segment next to its fees. Saving
 * stores the list on the merchant; syncing pushes the stored list to every store in Edge.
 */
const MerchantPricesModal: React.FC<MerchantPricesModalProps> = ({
    organizationId,
    merchant,
    canEdit,
    isInternal,
    hide,
    theme,
}: MerchantPricesModalProps) => {
    const {
        priceList,
        isLoadingPrices,
        pricesError,
        isSavingPrices,
        pricesSaved,
        savePricesError,
        isSyncingPrices,
        priceSyncResults,
        syncPricesError,
        loadPricesStarted,
        loadPricesSucceeded,
        loadPricesFailed,
        savePricesStarted,
        savePricesSucceeded,
        savePricesFailed,
        syncPricesStarted,
        syncPricesSucceeded,
        syncPricesFailed,
    } = useOnboardingStore();

    const defaults = useMemo(() => toPriceListDefaults(priceList), [priceList]);
    const form = useForm<PriceFormValues>({
        mode: 'onChange',
        defaultValues: toPriceListDefaults(undefined).values,
    });
    // The filters only decide what is shown: every market and segment stays mounted, so nothing typed is lost.
    const [market, setMarket] = useState('SE');
    const [segment, setSegment] = useState<SalesSegment>('B2C');
    const [onlyActive, setOnlyActive] = useState(false);

    const {
        formState: { errors, isDirty },
        handleSubmit,
        reset,
    } = form;

    // The stored list is fetched when the modal opens (GET .../prices) and fills the grid.
    useEffect(() => {
        getMerchantPrices(
            organizationId,
            merchant,
            loadPricesStarted,
            loadPricesSucceeded,
            loadPricesFailed
        );
    }, [organizationId, merchant, loadPricesStarted, loadPricesSucceeded, loadPricesFailed]);

    // The loaded (or just saved) list resets the grid; saving thereby also clears the dirty flag, even when the API
    // answers with a list equal to the one loaded.
    useEffect(() => {
        if (priceList) reset(defaults.values);
    }, [priceList, pricesSaved, defaults, reset]);

    const errorMessages = priceErrorMessages(errors);
    const dirty = isDirty;

    const isEmptyList = (priceList?.markets ?? []).length === 0;

    // Corporate customers are only offered in some markets; a market without the chosen segment shows consumers instead,
    // and the choice comes back on a market that has it.
    const segmentsOf = (country: PriceCountry) =>
        segmentsShownFor(country, defaults.values.products);
    const currentCountry = defaults.countries.find(country => country.code === market);
    const availableSegments = currentCountry ? segmentsOf(currentCountry) : salesSegments;
    const shownSegment = availableSegments.includes(segment) ? segment : 'B2C';

    const isBusy = isLoadingPrices || isSavingPrices || isSyncingPrices;
    const canSave = canEdit && dirty && errorMessages.length === 0 && !isBusy;
    const canSync = isInternal && !isBusy && !dirty && !pricesError;

    const save = handleSubmit(values =>
        saveMerchantPrices(
            organizationId,
            merchant,
            toMerchantPriceList(values, defaults.countries),
            savePricesStarted,
            savePricesSucceeded,
            savePricesFailed
        )
    );

    const sync = () =>
        syncMerchantPrices(
            organizationId,
            merchant,
            syncPricesStarted,
            syncPricesSucceeded,
            syncPricesFailed
        );

    const merchantName = hasMerchantName(merchant)
        ? merchant.name
        : formatOrganizationNumber(merchant.countryCode, merchant.organizationNumber);

    const renderContent = () => {
        if (isLoadingPrices) return <Spinner loading />;

        if (pricesError)
            return <ErrorMessage error={pricesError} errorHeader={ErrorType.LoadMerchantPrices} />;

        return (
            <FormProvider {...form}>
                <form onSubmit={event => event.preventDefault()}>
                    <FilterBar>
                        <FilterGroup role="group" aria-label="Market">
                            {defaults.countries.map(country => (
                                <FilterPill
                                    key={country.code}
                                    type="button"
                                    $active={country.code === market}
                                    aria-pressed={country.code === market}
                                    data-testid={`market-filter-${country.code}`}
                                    onClick={() => setMarket(country.code)}
                                >
                                    <FlagIcon countryCode={country.code} />
                                    {`${country.name} (${country.currency})`}
                                </FilterPill>
                            ))}
                        </FilterGroup>
                        <FilterGroup role="group" aria-label="Sales segment">
                            {availableSegments.map(salesSegment => (
                                <FilterPill
                                    key={salesSegment}
                                    type="button"
                                    $active={salesSegment === shownSegment}
                                    aria-pressed={salesSegment === shownSegment}
                                    data-testid={`segment-filter-${salesSegment}`}
                                    onClick={() => setSegment(salesSegment)}
                                >
                                    {segmentIcons[salesSegment]}
                                    {salesSegment}
                                </FilterPill>
                            ))}
                        </FilterGroup>
                        <FilterGroup role="group" aria-label="View">
                            <ViewToggle>
                                <Toggle
                                    id="toggle-only-active"
                                    checked={onlyActive}
                                    data-testid="toggle-only-active"
                                    onChange={() => setOnlyActive(value => !value)}
                                />
                                <label htmlFor="toggle-only-active">Only active methods</label>
                            </ViewToggle>
                        </FilterGroup>
                    </FilterBar>
                    <RefundSection>
                        <RefundFeeFields defaults={defaults.values.refunds} canEdit={canEdit} />
                    </RefundSection>
                    {defaults.countries.map(country => (
                        <div
                            key={country.code}
                            data-testid={`prices-market-${country.code}`}
                            hidden={country.code !== market}
                        >
                            <TableHeading>
                                {`${country.name} (${country.currency}) · ${shownSegment}`}
                            </TableHeading>
                            {segmentsOf(country).map(salesSegment => (
                                <SegmentContent
                                    key={salesSegment}
                                    hidden={salesSegment !== shownSegment}
                                >
                                    <CountryPriceTable
                                        segment={salesSegment}
                                        country={country}
                                        defaults={defaults.values}
                                        canEdit={canEdit}
                                        onlyActive={onlyActive}
                                        countries={defaults.countries}
                                        isEmptyList={isEmptyList}
                                        isBusy={isBusy}
                                    />
                                </SegmentContent>
                            ))}
                        </div>
                    ))}
                </form>
            </FormProvider>
        );
    };

    const showValidationSummary = canEdit && errorMessages.length > 0;
    // The outcome of the latest save or sync; the store keeps only one at a time. A saved confirmation goes away as soon
    // as the list is edited again, and a sync is confirmed only when the store found no failed store or product.
    const confirmation = priceSyncResults
        ? !syncPricesError && {
              message: "Prices synced to the merchant's stores.",
              testId: 'price-sync-success',
          }
        : pricesSaved && !dirty && { message: 'Price list saved.', testId: 'prices-saved' };
    // The failure of the latest save or sync, under the header of its action.
    const failure = savePricesError
        ? {
              error: savePricesError,
              header: ErrorType.SaveMerchantPrices,
              testId: 'save-prices-error',
          }
        : syncPricesError
        ? {
              error: syncPricesError,
              header: ErrorType.SyncMerchantPrices,
              testId: 'sync-prices-error',
          }
        : undefined;
    const hasStatus = showValidationSummary || confirmation || failure;

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>Price list</HeaderTitle>
                <MerchantName>{merchantName}</MerchantName>
            </ModalContainer>

            <ModalContainer position="content">{renderContent()}</ModalContainer>

            {/* Status sits in the fixed strip above the footer, so it is seen however far the grid is scrolled. */}
            {hasStatus && (
                <ModalContainer position="error">
                    {showValidationSummary && (
                        <ValidationError data-testid="prices-validation-summary">
                            {errorMessages.length === 1
                                ? errorMessages[0]
                                : `${errorMessages.length} fees need attention before the list can be saved`}
                        </ValidationError>
                    )}
                    {confirmation && (
                        <SavedBanner data-testid={confirmation.testId} role="status">
                            <CheckboxCircleFillIcon
                                color={theme.colors.success}
                                size={theme.icon.size.normal}
                            />
                            {confirmation.message}
                        </SavedBanner>
                    )}
                    {failure && (
                        <div data-testid={failure.testId}>
                            <ErrorMessage error={failure.error} errorHeader={failure.header} />
                        </div>
                    )}
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    <Button
                        type="button"
                        large
                        disabled={isSavingPrices || isSyncingPrices}
                        data-testid="close-prices-button"
                        onClick={hide}
                    >
                        {canEdit ? 'Cancel' : 'Close'}
                    </Button>
                    {isInternal && (
                        <Button
                            type="button"
                            large
                            disabled={!canSync}
                            title={dirty ? 'Save the list before syncing it' : undefined}
                            data-testid="sync-prices-button"
                            onClick={sync}
                        >
                            {isSyncingPrices ? (
                                <Spinner color={theme.colors.light} size={8} loading />
                            ) : (
                                'Sync prices'
                            )}
                        </Button>
                    )}
                    {canEdit && (
                        <Button
                            type="button"
                            primary
                            large
                            disabled={!canSave}
                            data-testid="save-prices-button"
                            onClick={save}
                        >
                            {isSavingPrices ? (
                                <Spinner color={theme.colors.light} size={8} loading />
                            ) : (
                                'Save'
                            )}
                        </Button>
                    )}
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

const MerchantName = styled.span`
    color: ${props => props.theme.colors.text.secondary};
`;

const FilterBar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    padding-bottom: 1.6rem;
    border-bottom: 0.1rem solid ${props => props.theme.colors.border};
`;

const FilterGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
`;

const FilterPill = styled.button<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 1.2rem;
    font-size: ${props => props.theme.text.size.small};
    font-family: ${props => (props.$active ? props.theme.text.font.medium : 'inherit')};
    color: ${props => props.theme.colors.text.primary};
    background: ${props => (props.$active ? props.theme.colors.subtle.light : 'transparent')};
    border: 0.1rem solid
        ${props => (props.$active ? props.theme.colors.primary : props.theme.colors.input.border)};
    border-radius: 2rem;
    cursor: pointer;

    &:hover {
        border-color: ${props => props.theme.colors.primary};
    }
`;

const RefundSection = styled.div`
    margin-top: 1.6rem;
`;

/** The saved confirmation as a banner like the error messages next to it, instead of the centered page confirmation. */
const SavedBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: ${props => props.theme.layout.padding.medium};
    color: ${props => props.theme.colors.text.primary};
    background: ${props => transparentize(0.9, props.theme.colors.success)};
    border: thin solid ${props => transparentize(0.4, props.theme.colors.success)};
    border-left: 0.5rem solid ${props => transparentize(0.4, props.theme.colors.success)};
`;

/** A view filter: only changes what the grid shows, never what is saved. */
const ViewToggle = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    font-size: ${props => props.theme.text.size.small};

    label {
        cursor: pointer;
    }
`;

const SegmentContent = styled.div`
    padding-top: 1.2rem;
`;

export default withTheme(MerchantPricesModal) as React.ComponentType<
    Omit<MerchantPricesModalProps, 'theme'>
>;
